import React, {useEffect, useRef, useState} from 'react';
import CFAdminDataTable from "../../components/CFAdminDataTable";
import ViewIcon from "../../assets/images/view-icon.svg";
import './CFAdminAttributes.scss'
import CFAdminNotification from '../../components/CFAdminNotification';
import VehicleConfigService from "../../services/VehicleConfigService";
import CFAdminModal from '../../components/CFAdminModal';
import CFAdminAttributeValueForm from './CFAdminAttributeValueForm';
import CFAdminConfirmDialog from '../../components/CFAdminConfirmDialog';
import AddIcon from "../../assets/images/add-icon.svg";
import DeleteIcon from "../../assets/images/delete.svg";
import EditIcon from "../../assets/images/icons8-edit.svg";

interface Attribute {
    attributeId: number;
    attributeName: string;
    attributeDescription: string;
    vinMapped: boolean;
}

const ViewAttributeValuesButton = ({props, titleDesc, onClick}: any) => (
    <button
        className="hover-cursor"
        title={titleDesc}
        onClick={() => onClick(props.row.original.attributeId)}
        data-testid={"attribute-" + props.row.original.attributeId}
        style={{background: 'none', border: 'none', padding: 0, cursor: 'pointer'}}
    >
        <img
            src={ViewIcon}
            alt="View"
            height="15"
        />
    </button>
);

const AttributeActionsCell = (props: any) => {
    const { onViewValues, onAddValue } = props.column;
    return (
        <div className="cell-buttons" style={{display: 'flex', gap: 8}}>
            <ViewAttributeValuesButton props={props} titleDesc="View Attribute Values"
                                       onClick={onViewValues}/>
            <button
                title="Add Attribute Value"
                onClick={() => onAddValue(props.row.original.attributeId)}
                style={{background: 'none', border: 'none', padding: 0, cursor: 'pointer'}}>
                <img src={AddIcon} alt="Add" height="15"/>
            </button>
        </div>
    );
};

const AttributeValueActionsCell = (props: any) => {
    const { onEditValue, onDeleteValue } = props.column;
    const row = props.row.original;
    return (
        <div style={{display: 'flex', gap: 8}}>
            <button
                title="Edit Attribute Value"
                onClick={() => onEditValue(row)}
                style={{background: 'none', border: 'none', padding: 0, cursor: 'pointer'}}>
                <img src={EditIcon} alt="Edit" height="15"/>
            </button>
            <button
                title="Delete Attribute Value"
                onClick={() => onDeleteValue(row)}
                style={{background: 'none', border: 'none', padding: 0, cursor: 'pointer'}}>
                <img src={DeleteIcon} alt="Delete" height="15"/>
            </button>
        </div>
    );
};

function CFAdminAttributes() {
    const childCompRef = useRef<any>();
    const [data, setData] = useState<Attribute[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    // State for attribute values modal
    const [isValuesOpen, setIsValuesOpen] = useState(false);
    const [valuesColumns, setValuesColumns] = useState<any[]>([]);
    const [valuesData, setValuesData] = useState<any[]>([]);
    const [valuesHeading, setValuesHeading] = useState('Attribute Values');
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<any>(null);
    const [isValueFormOpen, setIsValueFormOpen] = useState(false);
    const [valueFormInitial, setValueFormInitial] = useState<any>(null);


    const retrieveAttributes = () => {
        VehicleConfigService.getAttributesAll()
            .then(response => {
                setData(response.data.attributes);
            })
            .catch(e => {
                console.log(e);
            });
    };
    useEffect(() => {
        retrieveAttributes();
    }, []);

    const toggleModal = () => {
        setIsOpen(!isOpen);
    }

    const toggleValuesModal = () => {
        setIsValuesOpen(!isValuesOpen);
    }

    const confirmSuccessMessage = async (value: any) => {
        try {
            childCompRef.current?.successMessage(value);
        } catch (err) {
            console.error(err);
        }
    };
    const confirmErrorMessage = async (value: any) => {
        try {
            childCompRef.current?.failureMessage(value);
        } catch (err) {
            console.error(err);
        }
    };


    const handleDeleteAttributeValue = async (row: any) => {
        // Open application confirm dialog (CFAdminConfirmDialog)
        setDeleteTarget(row);
        setIsDeleteConfirmOpen(true);
    };

    const confirmDeleteAttributeValue = async () => {
        try {
            if (!deleteTarget) return;
            await VehicleConfigService.deleteVehicleAttributeValue(deleteTarget.id);
            const updated = valuesData.filter(v => v.id !== deleteTarget.id);
            setValuesData(updated);
            setIsDeleteConfirmOpen(false);
            setDeleteTarget(null);
            await confirmSuccessMessage('Attribute value deleted successfully.');
        } catch (e) {
            console.error(e);
            setIsDeleteConfirmOpen(false);
            setDeleteTarget(null);
            await confirmErrorMessage('Failed to delete attribute value.');
        }
    };

    const handleViewAttributeRowClick = async (attributeId: number) => {
        // Fetch attribute values for the selected attribute and open modal with CFAdminDataTable
        try {
            const request = {attributeIds: [attributeId]};
            const response: any = await VehicleConfigService.getVehicleAttributeValuesWithIds(request);
            const respData = response?.data ?? {};
            let valuesArray = respData.attributeValues;
            if (!Array.isArray(valuesArray)) {
                // if the API returned an object with keys like makes/models/years, try to flatten
                valuesArray = [];
            }

            const rows = valuesArray.map((el: any) => ({
                id: el.attributeValueId ?? el.id,
                attributeId: attributeId,
                description: el.attributeValueDescription ?? el.description ?? ''
            }));

            setValuesData(rows);
            setValuesColumns([
                {Header: 'Id', accessor: 'id'},
                {Header: 'Description', accessor: 'description'},
                {
                    Header: 'Actions',
                    onEditValue: (row: any) => {
                        setValueFormInitial({
                            attributeValueId: row.id,
                            attributeId: row.attributeId || null,
                            attributeValueDescription: row.description
                        });
                        setIsValueFormOpen(true);
                    },
                    onDeleteValue: handleDeleteAttributeValue,
                    Cell: AttributeValueActionsCell
                }
            ]);
            setValuesHeading('Attribute Values');
            setIsValuesOpen(true);
        } catch (e) {
            console.error(e);
            setValuesData([]);
            setValuesHeading('Attribute Values');
            setIsValuesOpen(true);
        }
    }

    const refreshValuesForAttribute = async (attributeId: number) => {
        try {
            const request = {attributeIds: [attributeId]};
            const response: any = await VehicleConfigService.getVehicleAttributeValuesWithIds(request);
            const respData = response?.data ?? {};
            let valuesArray = respData.attributeValues || respData.values || respData;
            if (!Array.isArray(valuesArray)) valuesArray = [];
            const rows = valuesArray.map((el: any) => ({
                id: el.attributeValueId ?? el.id,
                attributeId: attributeId,
                description: el.attributeValueDescription ?? el.description ?? ''
            }));
            setValuesData(rows);
        } catch (e) {
            console.error(e);
        }
    }

    const columns = [
        {
            Header: "Id",
            accessor: 'attributeId'
        },
        {
            Header: "Name",
            accessor: 'attributeName'
        },
        {
            Header: "Description",
            accessor: 'attributeDescription'
        },
        {
            Header: "VIN Mapped",
            accessor: 'vinMapped',
            Cell: ({value}: { value: boolean }) => (value ? "True" : "False")
        },
        {
            Header: "Actions",
            onViewValues: handleViewAttributeRowClick,
            onAddValue: (attributeId: number) => {
                setValueFormInitial({attributeId});
                setIsValueFormOpen(true);
            },
            Cell: AttributeActionsCell
        }
    ];

    return (
        <div className="attribute-component">
            <h2 className="fomo-type--title">Manage Attributes</h2>
            <CFAdminDataTable
                columns={columns}
                data={data}
                toggleModal={toggleModal}
                textModal={'Add Attribute'}
                showAdd={false}/>
            <CFAdminModal
                isOpen={isValuesOpen}
                toggle={toggleValuesModal}
                isAdd={true}
                title={valuesHeading}>
                <CFAdminDataTable
                    columns={valuesColumns}
                    data={valuesData}
                    showAdd={false}
                />
            </CFAdminModal>
            <CFAdminModal
                isOpen={isValueFormOpen}
                toggle={() => {
                    setIsValueFormOpen(!isValueFormOpen);
                    setValueFormInitial(null);
                }}
                isAdd={true}
                title={valueFormInitial?.attributeValueId ? 'Edit Attribute Value' : 'Add Attribute Value'}>
                {valueFormInitial && (
                    <CFAdminAttributeValueForm
                        attributeValueId={valueFormInitial.attributeValueId}
                        attributeId={valueFormInitial.attributeId ?? null}
                        attributeValueDescription={valueFormInitial.attributeValueDescription}
                        toggleModal={() => {
                            setIsValueFormOpen(false);
                            setValueFormInitial(null);
                        }}
                        refreshData={() => {
                            // if attributeId present refresh values for that attribute
                            const aid = valueFormInitial.attributeId;
                            if (aid) refreshValuesForAttribute(aid);
                        }}
                        successMessage={confirmSuccessMessage}
                        errorMessage={confirmErrorMessage}
                    />
                )}
            </CFAdminModal>
            <CFAdminConfirmDialog
                isOpen={isDeleteConfirmOpen}
                onClose={() => {
                    setIsDeleteConfirmOpen(false);
                    setDeleteTarget(null);
                }}
                onConfirm={confirmDeleteAttributeValue}
                title={deleteTarget ? `Delete Attribute Value: ${deleteTarget.description}` : 'Delete Attribute Value'}
                text={'Are you sure you want to delete this Attribute Value?'}
            />
            <CFAdminNotification ref={childCompRef}/>
        </div>
    );
}

export default CFAdminAttributes;